/* #region Search Reducer Description */
/** Search Reducer
 *
 * searchWithin is the place option associated with the clicked "places within " button.
 * searchByLocation is the lat,lon distance and distance type ("Miles", "Kilometers") to search from within.
 * confidenceLevel: is one of the following user confidence levels ["ACCEPTED", "PROVISIONAL", "CERTIFIED"].
 * directChildrenOnly: whether to filter on direct children in search results.
 * placeType: If included only include places matching placeType.id and placeType.label.
 * yearRange: If included filter results by year range.
 * startYear: Used to formulate yearRange.
 * endYear: Used to formulate yearRange.
 * exactYear: If included filter results by exact year.
 *
 * Places 2.0 request params see (https://icseng.atlassian.net/wiki/x/nYP2Bw)
 *  -- BASIC TEXT SEARCH --
 * text: string, (e.g "Salt Lake City")
 *
 * -- SEARCH WITHIN --
 * reqParents: string,  Parent place Id (e.g. "1" for United States)
 *
 * -- FILTER DIRECT CHILDREN --
 * reqDirParents: string,
 *
 * -- FILTER BY CONFIDENCE LEVEL
 * valType: string, [e.g "non_val_only", or "val_only"]
 * pubType: string, [e.g "non_pub_only"]
 *
 * -- FILTER BY YEAR RANGE
 * reqYears: string, [e.g. "1800-1900"]
 *
 * --- SEARCH BY LOCATION
 * lng: string, latitude (e.g. "115.00")
 * lat: string, longitude (e.g. "-45,00")
 * distance: string  --- SEARCH BY LOCATION includes distance type character 'm' for Miles and 'km" for Kilometers (eg. '15m')
 *
 * -- SHOW PLACES WITHIN Flag --
 * includeIsParent: boolean, [e.g. "true"]
 */
/* #endregion */

import debugFactory, { debugVerbose as debugVerboseFactory } from '@fs/zion-debug'
import { getInputTextFromSearchWithin } from './searchHelper'

const debug = debugFactory('standards:places:searchReducer')
const debugVerbose = debugVerboseFactory('standards:places:searchReducer')

const PLACE_TYPE_GROUPS = {
  26: 'Countries',
  27: 'States and Provinces',
  28: 'Counties and Districts',
  8: 'Cities and Towns',
  3: 'Churches and Church Parishes',
}
/* #region Default Values */
const SEARCH_DEFAULT = {
  criteria: {
    /**
     * May contain any combination of the following fields:
     *        KEY | CORRESPONDING INPUT(s)
     * --------------------------------------
     *       text | Place Name
     * reqParents | Places Within
     *   reqTypes | Place Type
     *   reqYears | Exact Year, Year Start, Year End
     *        lat | (from "Set Location" feature)
     *        lng | (from "Set Location" feature)
     *   distance | Distance from Location (input and dropdown values are combined)
     */
  },
  isLoading: false,
  noSearchesPerformed: true,
  validResults: true,
  results: undefined,
  selectedPlace: undefined,
  toggleToUnfocused: false,
  idList: [],
  resultCount: 0,
  resultsFound: 0,
  filterOverlayIsOpen: false,
}

const MAP_DEFAULT = {
  mapIsClickable: false,
  map: undefined,
  resetZoom: false,
}

const DETAILS_DEFAULT = {
  details: {
    id: null,
    zoomLevel: null,
    hoveredId: null,
    placeId: null,
    placeDetailsAreLoading: false,
    validResults: true,
    timeline: [],
    heading: {
      fullName: '',
      placeType: '',
    },
    placeDetails: {
      basicInfo: {},
      researchLinks: [],
      historicalInfo: [],
      altNames: [],
      placesWithin: [],
      relatedPlace: [],
      citations: [],
      placeData: [],
    },
  },
}

const EXPANDER_OPEN_DEFAULT = {
  expanders: {
    basicInfo: true,
    historicalInfo: false,
    researchLinks: false,
    altNames: false,
    displayNames: false,
    relatedPlaces: false,
    citations: false,
  },
  scrollTop: null,
}

export const DEFAULT_SEARCH_STATE = {
  ...SEARCH_DEFAULT,
  ...DETAILS_DEFAULT,
  ...MAP_DEFAULT,
  ...EXPANDER_OPEN_DEFAULT,
}
/* #endregion */

export default function searchReducer(state, action) {
  debugVerbose('searchReducer:', state)
  debug('action:', action)

  let newState = { ...state }
  switch (action.type) {
    /* #region Search */
    case 'UPDATE_SEARCH_LOADING':
      newState = { ...state, isLoading: action.isLoading }
      break
    case 'UPDATE_SEARCH_RESULTS':
      newState = {
        ...state,
        results: action.results,
        resultsMap: action.resultsMap,
        idList: action.idList,
        resultCount: action.resultCount,
        resultsFound: action.resultsFound,
        noSearchesPerformed: false,
        isLoading: false,
        details: {
          ...state.details,
          skipPan: false, // Clear skipPan on new search results to allow fitToChildren
        },
      }
      break
    case 'CLEAR_RESULTS':
      newState = { ...state, results: [], idList: [], noSearchesPerformed: true }
      break
    case 'UPDATE_CURRENT_PAGE':
      newState = { ...state, pagenum: action.pagenum }
      break
    case 'SEARCH_WITHIN':
      {
        // FIX preserve previous state
        const searchWithin = action.searchWithin
        const { primaryText, place } = searchWithin
        const criteria = {
          reqParents: place.id,
          primaryText,
        }

        newState = {
          ...state,
          ...SEARCH_DEFAULT,
          place,
          searchWithin,
          criteria,
          searchTypeaheadInputText: getInputTextFromSearchWithin(searchWithin),
          validResults: false,
          resetZoom: true,
        }
      }

      break

    case 'SEARCH_BY_TEXT':
      newState = { ...state, ...SEARCH_DEFAULT, criteria: { ...state.criteria }, ...DETAILS_DEFAULT }
      break
    /* #endregion */

    /* #region SearchFilter */
    case 'SET_PLACE_TYPE':
      {
        const { placeType } = action
        debug('placeType ', placeType)

        const criteria = { ...state.criteria }
        delete criteria.reqTypes
        delete criteria.reqTypeGroups
        delete criteria.reqTypeLabel

        if (placeType) {
          if ('id' in placeType && placeType.id && placeType.id !== 0) {
            // TODO: This is fragile, figure out best way to differentiate
            if (PLACE_TYPE_GROUPS[placeType.id]) {
              criteria.reqTypeGroups = placeType.id
            } else {
              criteria.reqTypes = placeType.id
            }
          }
          if ('primaryText' in placeType && placeType.primaryText) {
            criteria.reqTypeLabel = placeType.primaryText
          }
        }

        newState = {
          ...state,
          placeType,
          criteria,
          validResults: false,
        }
      }

      break

    case 'SET_START_YEAR':
      {
        const yearRange = `${action.startYear}-${state.endYear || new Date().getFullYear()}`
        newState = {
          ...state,
          yearRange,
          startYear: action.startYear,
          exactYear: undefined,
          criteria: {
            ...state.criteria,
            reqYears: yearRange,
          },
          validResults: false,
        }
      }

      break
    case 'SET_END_YEAR':
      {
        const yearRange = `${state.startYear || 0}-${action.endYear}`
        newState = {
          ...state,
          yearRange,
          endYear: action.endYear,
          exactYear: undefined,
          criteria: {
            ...state.criteria,
            reqYears: yearRange,
          },
          validResults: false,
        }
      }

      break
    case 'SET_EXACT_YEAR':
      newState = {
        ...state,
        yearRange: action.exactYear,
        exactYear: action.exactYear,
        startYear: undefined,
        endYear: undefined,
        criteria: {
          ...state.criteria,
          reqYears: action.exactYear,
        },
        validResults: false,
      }

      break
    case 'SET_CONFIDENCE_LEVEL':
      {
        const { confidenceLevel } = action
        const criteria = { ...state.criteria }
        delete criteria.valType
        delete criteria.pubType

        switch (confidenceLevel) {
          case 'ACCEPTED':
            criteria.valType = 'non_val_only'
            criteria.pubType = 'pub_only'
            break
          case 'CERTIFIED':
            criteria.valType = 'val_only'
            criteria.pubType = 'pub_only'
            break
          case 'PROVISIONAL':
            criteria.pubType = 'non_pub_only'
            criteria.valType = 'non_val_only'
            break
          default:
            break
        }

        newState = { ...state, confidenceLevel, criteria, validResults: false }
      }

      break
    case 'SET_DIRECT_CHILDREN_ONLY':
      {
        const { placeId, directChildrenOnly } = action
        const criteria = { ...state.criteria }
        delete criteria.reqDirParents

        if (directChildrenOnly) {
          // this id needs to be the parentJuris id NOT the place id
          criteria.reqDirParents = placeId
        }

        newState = {
          ...state,
          directChildrenOnly,
          criteria,
          validResults: false,
        }
      }
      break

    case 'SUBMITTED_FILTER_OVERLAY':
    case 'CLEARED_ALL_FILTERS':
    case 'CANCELLED_FILTER_OVERLAY':
      newState = {
        ...state,
        filterOverlayIsOpen: false,
      }
      break
    /* #endregion */

    /* #region PlacesMap */
    case 'MAP_LOADED':
      newState = { ...state, map: action.map }
      break
    case 'OMS_LOADED':
      newState = { ...state, oms: action.oms }
      break
    case 'UPDATE_SHOW_BORDERS':
      newState = { ...state, showBorders: action.showBorders }
      break
    case 'UPDATE_MAP_CLICK_LOCATION':
      {
        const mapClickLocation = { ...state.mapClickLocation, ...action.mapClickLocation }
        newState = { ...state, mapClickLocation }
      }

      break
    case 'UPDATE_BOUNDARY':
      {
        const details = { ...state.details, boundary: action.boundary }
        newState = { ...state, details }
      }

      break
    case 'UPDATE_HOVERED':
      {
        const details = {
          ...state.details,
          hoveredId: action.hoveredId,
          skipPan: action.skipPan,
          minZoom: undefined, // Clear minZoom on hover (don't zoom for hover)
        }
        newState = { ...state, details }
      }

      break
    case 'CLEAR_HOVERED':
      {
        // When clearing hover, optionally set minZoom if provided (for focus-from-list)
        // Otherwise undefined (for hover clear, X button, etc.)
        const details = {
          ...state.details,
          hoveredId: null,
          skipPan: action.skipPan,
          minZoom: action.minZoom !== undefined ? action.minZoom : undefined,
        }
        newState = { ...state, details }
      }

      break
    /* #endregion */

    /* #region fetching */
    case 'UPDATE_LOADING':
      {
        const details = { ...state.details, placeDetailsAreLoading: action.placeDetailsAreLoading }
        debug('placeDetailsAreLoading:', action.placeDetailsAreLoading)
        newState = { ...state, details }
      }

      break
    case 'FETCHING':
      {
        const details = { ...state.details, placeDetailsAreLoading: true }
        newState = { ...state, details }
      }

      break
    case 'FETCHED':
      {
        const { id, zoomLevel, placeId, timeline, heading, placeDetails, historicPlaces } = action.details || {}
        const details = {
          ...state.details,
          placeDetailsAreLoading: false,
          id,
          zoomLevel,
          placeId,
          timeline,
          heading,
          placeDetails,
          historicPlaces,
        }

        debugVerbose(`details: ${JSON.stringify(details)}`)
        newState = { ...state, details }
      }
      break
    /* #endregion */

    /* #region Place Details */
    case 'BASIC_INFO_OPEN':
      {
        const expanders = { ...state.expanders, basicInfo: action.basicInfo }
        newState = { ...state, expanders }
      }

      break
    case 'HISTORICAL_INFO_OPEN':
      {
        const expanders = { ...state.expanders, historicalInfo: action.historicalInfo }
        newState = { ...state, expanders }
      }

      break
    case 'RESEARCH_LINKS_OPEN':
      {
        const expanders = { ...state.expanders, researchLinks: action.researchLinks }
        newState = { ...state, expanders }
      }

      break
    case 'ALTERNATE_NAMES_OPEN':
      {
        const expanders = { ...state.expanders, altNames: action.altNames }
        newState = { ...state, expanders }
      }

      break
    case 'DISPLAY_NAMES_OPEN':
      {
        const expanders = { ...state.expanders, displayNames: action.displayNames }
        newState = { ...state, expanders }
      }

      break
    case 'RELATED_PLACES_OPEN':
      {
        const expanders = { ...state.expanders, relatedPlaces: action.relatedPlaces }
        newState = { ...state, expanders }
      }

      break
    case 'CITATIONS_OPEN':
      {
        const expanders = { ...state.expanders, citations: action.citations }
        newState = { ...state, expanders }
      }

      break
    /* #endregion */

    case 'UPDATE_SELECTED_PLACE':
      {
        const toggleToUnfocused = Boolean(
          state?.selectedPlace?.id && state?.selectedPlace?.id === action.selectedPlace?.id
        )
        const selectedPlace = toggleToUnfocused ? null : action.selectedPlace
        const hoveredId = toggleToUnfocused ? null : action.selectedPlace?.id || null
        debug(
          `toggleToUnfocused: ${toggleToUnfocused}, selectedPlace: ${JSON.stringify(
            selectedPlace
          )}, hoveredId: ${hoveredId}`
        )
        newState = { ...state, selectedPlace, hoveredId, toggleToUnfocused, resetZoom: true }
      }
      break
    case 'CLEAR_SELECTED_PLACE':
      {
        const details = { ...state.details, minZoom: undefined }
        newState = { ...state, selectedPlace: null, resetZoom: true, toggleToUnfocused: false, details }
      }
      break

    case 'SET_SCROLL':
      newState = { ...state, scrollTop: action.scrollTop }
      break
    default:
      throw new Error(`Unhandled action type: ${action.type}`)
  }
  debugVerbose('newSearchState:', newState)
  return newState
}
